/* ==========================================================================
   Product card — rotate the thumbnail through the colour images

   Cycles each card's main image through that product's per-variant images
   every 3 seconds, so a shopper sees every colour without hovering.

   WHERE THE IMAGES COME FROM
   Nothing new is rendered. product-card.liquid already emits, per card:

     .product_media .product_gallery-item img.image-class   the visible image
     <template data-media-template-…>                       one .product_gallery-item
                                                            per variant that has a
                                                            featured image

   The template exists so the colour swatches can swap the picture. Reading it
   here means no Liquid change and no extra markup — and it is the same set of
   images the swatches would show, so the rotation and the swatches agree.

   Variants are colour x size, so the template repeats each colour's image once
   per size. Frames are de-duplicated by src, which collapses those back to one
   entry per distinct picture.

   WHAT IT COSTS
   Rotating means loading more than one image per card, so three things keep
   that in check:
     - only cards intersecting the viewport run a timer
     - the next image is fetched one step ahead, not all of them up front
     - a hidden tab stops every timer

   WHEN IT STOPS
     - on hover, so Kidu's hover-swap to the second image still works
     - permanently after any click inside the card: that is a swatch press or a
       Quick Add, and continuing would overwrite the image the shopper chose
     - entirely under prefers-reduced-motion

   Auto-advancing content needs a way to stop it (WCAG 2.2.2); hover and the
   click rule are that mechanism.
   ========================================================================== */

(function () {
  'use strict';

  var INTERVAL = 3000;
  var STAGGER = 500; // spread start times so the grid doesn't flip in unison
  var STYLE_ID = 'pcr-style';
  var FLAG = '__pcrBound';

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Five lines of CSS, injected rather than shipped as a separate asset so the
     whole feature stays one file. The selector is (0,2,1) and Kidu's hover rule
     is (0,4,1), so the hover swap always wins — and rotation is paused on hover
     regardless. */
  function injectStyle() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '@keyframes pcr-fade{from{opacity:.25}to{opacity:1}}' +
      '.product_gallery-item img.pcr-swap{animation:pcr-fade 320ms ease-out}';
    document.head.appendChild(style);
  }

  var rotators = [];
  var index = 0;

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        var rotator = entry.target.__pcrRotator;
        if (!rotator) return;
        if (entry.isIntersecting) rotator.start();
        else rotator.stop();
      });
    },
    { rootMargin: '100px' }
  );

  function readFrames(card, main) {
    var frames = [{ src: main.getAttribute('src'), srcset: main.getAttribute('srcset') }];
    var seen = {};
    seen[frames[0].src] = true;

    var template = card.querySelector('template');
    if (!template || !template.content) return frames;

    var candidates = template.content.querySelectorAll('.product_gallery-item img.image-class');
    for (var i = 0; i < candidates.length; i++) {
      var src = candidates[i].getAttribute('src');
      if (!src || seen[src]) continue;
      seen[src] = true;
      frames.push({ src: src, srcset: candidates[i].getAttribute('srcset') });
    }
    return frames;
  }

  function createRotator(media, main, frames, offset) {
    var current = 0;
    var timer = null;
    var running = false;
    var dead = false;

    function preload(frame) {
      var img = new Image();
      if (frame.srcset) img.srcset = frame.srcset;
      img.src = frame.src;
    }

    function advance() {
      current = (current + 1) % frames.length;
      var frame = frames[current];

      // Already fetched by the previous tick, so this paints without a gap.
      if (frame.srcset) main.setAttribute('srcset', frame.srcset);
      main.setAttribute('src', frame.src);

      main.classList.remove('pcr-swap');
      // Reading offsetWidth forces a reflow, which restarts the animation
      // instead of the class re-add being coalesced into one frame.
      void main.offsetWidth;
      main.classList.add('pcr-swap');

      preload(frames[(current + 1) % frames.length]);
    }

    function start() {
      if (running || dead || document.hidden) return;
      running = true;
      // Fetch the first replacement now: the card has just scrolled into view,
      // so there are INTERVAL milliseconds to spare before it is needed.
      preload(frames[(current + 1) % frames.length]);
      timer = setTimeout(function tick() {
        advance();
        timer = setTimeout(tick, INTERVAL);
      }, INTERVAL + offset);
    }

    function stop() {
      running = false;
      clearTimeout(timer);
      timer = null;
    }

    function kill() {
      stop();
      dead = true;
      observer.unobserve(media);
    }

    media.addEventListener('mouseenter', stop);
    media.addEventListener('mouseleave', start);

    /* A click here is a swatch or a Quick Add — both set the image
       deliberately, so the rotation must not take it back. Clicking the
       picture itself navigates away, so this costs nothing there. */
    var card = media.closest('.product-column') || media.parentNode;
    if (card) card.addEventListener('click', kill);

    return { start: start, stop: stop };
  }

  function bind() {
    injectStyle();

    var medias = document.querySelectorAll('.product_media');

    medias.forEach(function (media) {
      if (media[FLAG]) return;

      var main = media.querySelector('.product_gallery-item img.image-class');
      if (!main) return;

      var card = media.closest('product-card') || media.closest('.product-column');
      if (!card) return;

      var frames = readFrames(card, main);
      if (frames.length < 2) return; // one picture, nothing to rotate

      media[FLAG] = true;

      var rotator = createRotator(media, main, frames, (index % 6) * STAGGER);
      index++;
      media.__pcrRotator = rotator;
      rotators.push(rotator);
      observer.observe(media);
    });
  }

  /* A background tab should not be fetching images on a timer. */
  document.addEventListener('visibilitychange', function () {
    rotators.forEach(function (rotator) {
      if (document.hidden) rotator.stop();
    });
  });

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  ready(function () {
    bind();

    // The theme editor replaces whole sections on a settings change.
    document.addEventListener('shopify:section:load', bind);

    /* "Click to load" and infinite pagination append cards through
       assets/pagination.js, and the filters replace the grid — none of which
       dispatch an event to hook. Watching for added nodes is the only reliable
       signal. Only childList is observed, so the attribute changes this file
       makes when swapping an image cannot feed back into it. */
    var rescan = null;
    new MutationObserver(function () {
      clearTimeout(rescan);
      rescan = setTimeout(bind, 200);
    }).observe(document.body, { childList: true, subtree: true });
  });
})();